import { type ColumnDef } from '@tanstack/react-table';
import { MessageSquareText } from 'lucide-react';
import { cn, formatCount } from '@/lib/utils';
import { ColumnType } from '@/types/column';
import { GalleryView } from '@/types';

interface CommentCountColumnOptions<T> {
  id?: string;
  accessorKey: keyof T;
  enableSorting?: boolean;
  enableHiding?: boolean;
  minSize?: number;
  maxSize?: number;
  label: string;
  galleryView: GalleryView;
  size?: number;
  cellClassName?: string;
  headerClassName?: string;
  dataCy?: string;
}

export const CommentCountColumn = <T extends Record<string, any>>(opts: CommentCountColumnOptions<T>): ColumnDef<T> => {
  const {
    id = ColumnType.CommentCount,
    accessorKey = 'commentCount',
    label = 'Comments',
    galleryView,
    size = 100,
    cellClassName,
    headerClassName,
    enableSorting = false,
    enableHiding = true,
    minSize = 30,
    maxSize = 120,
    dataCy,
  } = opts;

  return {
    id,
    accessorKey,
    enableSorting,
    enableHiding,
    minSize,
    maxSize,
    ...((cellClassName ?? headerClassName) && {
      meta: {
        ...(cellClassName && { cellClassName: cellClassName }),
        ...(headerClassName && { headerClassName: headerClassName }),
      },
    }),
    size,
    header: () => label,
    cell: ({ getValue, row }) => {
      const count = getValue() as number | null | undefined;
      const formattedCount = count ? formatCount(count) : 0;

      if (galleryView === GalleryView.TILE) {
        return formattedCount ? (
          <div className="ml-auto flex items-center gap-x-1" data-cy={dataCy ?? `${galleryView}-${row.index}-${id}`}>
            <MessageSquareText className="icon-3 shrink-0 text-icon-base" />
            <span className="text-xs text-text-gray">{formattedCount}</span>
          </div>
        ) : null;
      }

      return (
        <p
          className={cn('whitespace-nowrap text-text-gray')}
          data-cy={dataCy ?? `${galleryView ? galleryView : 'list'}-${row.index}-${id}`}
        >
          {count ?? 0}
        </p>
      );
    },
  };
};
